class ScxProductImageSwiper extends HTMLElement {
  constructor() {
    super();

    this.swiperId = this.id;
    this.dataSelector = this.getAttribute('data-selector');
    this.dataTargetSelector = this.getAttribute('data-target-selector');
    const loadingElem = this.getAttribute('data-loading-elem');
    this.ignoreLazyLoading = this.shouldIgnoreLazyLoad();

    // Cache for performance
    this.swiperInstance = null;
    this.isInitializing = false;
    this.isInitialized = false;
    this.observer = null;
    
    // Cache DOM elements - will be set when needed
    this.loadingDiv = null;
    this.swiperDiv = null;
    this.firstViewBtn = null;
    this.secondViewBtn = null;
    
    // Cache options
    this._cachedOptions = null;
    this._paginationOptions = null;
    this._mousewheelOptions = null;
    
    // Bind methods for better performance
    this.initSwiperInstance = this.initSwiperInstance.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.moveToFirstSlide = this.moveToFirstSlide.bind(this);
    this.moveToSecondSlide = this.moveToSecondSlide.bind(this);
  }

  shouldIgnoreLazyLoad() {
    const dLazy = this.getAttribute('data-lazy');
    // Ignoring Lazy Load must be explicit
    if (dLazy === null || dLazy === undefined || dLazy === '') {
      return true;
    }
    return dLazy !== 'true';
  }

  connectedCallback() {
    // Immediate initialization for critical swipers
    if (this.ignoreLazyLoading) {
      this.initializeImmediately();
    } else {
      this.initLazyLoading();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeImmediately() {
    if (this.isInitialized || this.isInitializing) return;
    
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      this.initSwiperInstance();
    } else {
      // Use more efficient event listener
      const onReady = () => {
        this.initSwiperInstance();
        document.removeEventListener('DOMContentLoaded', onReady);
      };
      document.addEventListener('DOMContentLoaded', onReady, { once: true, passive: true });
    }
  }

  initLazyLoading() {
    if (this.isInitialized || this.isInitializing) return;
    
    // More efficient intersection observer
    const targetElement = document.querySelector(`.${this.dataTargetSelector}`);
    if (!targetElement) {
      console.warn('Target element not found for lazy loading:', this.dataTargetSelector);
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersection, {
      root: null,
      rootMargin: '50px 0px',
      threshold: 0
    });

    this.observer.observe(targetElement);
    
    // Fallback: Initialize after a reasonable delay if not intersected
    setTimeout(() => {
      if (!this.isInitialized && !this.isInitializing) {
        this.initSwiperInstance();
      }
    }, 3000);
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (entry.isIntersecting && !this.isInitialized && !this.isInitializing) {
      console.debug('Creating optimized product image swiper instance...');
      this.initSwiperInstance();
      
      // Stop observing immediately
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  initSwiperInstance() {
    if (this.isInitialized || this.isInitializing) return;
    
    this.isInitializing = true;
    
    const selectorString = `.${this.dataSelector}`;
    const swiperElement = document.querySelector(selectorString);
    
    if (!swiperElement) {
      console.warn('Product image swiper element not found:', selectorString);
      this.isInitializing = false;
      return;
    }

    // Cache DOM elements
    const loadingElem = this.getAttribute('data-loading-elem');
    this.loadingDiv = document.querySelector(`.${loadingElem}`);
    this.swiperDiv = swiperElement;

    // Cleanup any existing instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    // Remove loading state immediately for better UX
    this.updateLoadingState();

    // Use requestAnimationFrame for smoother initialization
    requestAnimationFrame(() => {
      try {
        this.swiperInstance = new Swiper(selectorString, this.getSwiperOptions());
        this.isInitialized = true;
        
        // Setup view buttons after swiper is initialized
        this.setupViewButtons();
      } catch (error) {
        console.error('Failed to initialize Product Image Swiper:', error);
      } finally {
        this.isInitializing = false;
      }
    });
  }

  setupViewButtons() {
    // Cache and setup first view button
    if (!this.firstViewBtn) {
      this.firstViewBtn = document.querySelector('.first-view');
      if (this.firstViewBtn) {
        this.firstViewBtn.addEventListener('click', this.moveToFirstSlide, { passive: true });
      }
    }
    
    // Cache and setup second view button
    if (!this.secondViewBtn) {
      this.secondViewBtn = document.querySelector('.second-view');
      if (this.secondViewBtn) {
        this.secondViewBtn.addEventListener('click', this.moveToSecondSlide, { passive: true });
      }
    }
  }

  moveToFirstSlide() {
    if (this.swiperInstance && this.isInitialized) {
      this.swiperInstance.slideTo(0);
    }
  }

  moveToSecondSlide() {
    if (this.swiperInstance && this.isInitialized) {
      this.swiperInstance.slideTo(1);
    }
  }

  updateLoadingState() {
    if (this.loadingDiv) {
      this.loadingDiv.setAttribute('data-loaded', 'true');
    }
    if (this.swiperDiv) {
      this.swiperDiv.setAttribute('data-loading', 'false');
    }
  }

  cleanup() {
    // Remove event listeners
    if (this.firstViewBtn) {
      this.firstViewBtn.removeEventListener('click', this.moveToFirstSlide);
      this.firstViewBtn = null;
    }
    if (this.secondViewBtn) {
      this.secondViewBtn.removeEventListener('click', this.moveToSecondSlide);
      this.secondViewBtn = null;
    }
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    this.isInitialized = false;
    this.isInitializing = false;
  }

  getSwiperOptions = () => {
    // Cache options to avoid recreating the object every time
    if (this._cachedOptions) {
      return this._cachedOptions;
    }

    this._cachedOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      nested: true,
      updateOnWindowResize: true,
      observer: false,
      observeParents: false,
      runCallbacksOnInit: false,
      ...this.getPaginationOptions(),
      on: {
        init: () => {
          this.updateLoadingState();
        },
        beforeInit: () => {
          // Pre-initialize optimizations
          this.updateLoadingState();
        }
      },
    };

    return this._cachedOptions;
  };

  getMousewheelOptions = () => {
    if (this._mousewheelOptions !== undefined) {
      return this._mousewheelOptions;
    }

    const mwElem = this.getAttribute('data-mousewheel');

    this._mousewheelOptions = mwElem === 'true' ? {
      mousewheel: {
        forceToAxis: true,
      },
    } : {};

    return this._mousewheelOptions;
  };

  getPaginationOptions = () => {
    if (this._paginationOptions !== undefined) {
      return this._paginationOptions;
    }

    const mwElem = this.getAttribute('data-pagination-elem');

    this._paginationOptions = mwElem ? {
      pagination: {
        el: mwElem,
        clickable: true,
        dynamicBullets: this.getAttribute('data-dynamic-bullets') === 'true',
      },
    } : {};

    return this._paginationOptions;
  };
}

// Enhanced event handling for Shopify theme editor
document.addEventListener('shopify:section:load', (event) => {
  const productImageSwipers = event.target.querySelectorAll('scx-product-image-swiper');
  productImageSwipers.forEach(swiper => {
    if (swiper.swiperInstance) {
      swiper.cleanup();
      swiper.connectedCallback();
    }
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  const productImageSwipers = event.target.querySelectorAll('scx-product-image-swiper');
  productImageSwipers.forEach(swiper => {
    swiper.cleanup();
  });
});

customElements.define('scx-product-image-swiper', ScxProductImageSwiper);
